import { useEffect, useState } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    const data = new URLSearchParams();
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d6a8ggma728k8ctu35dg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: data.toString(),
      });

      if (response.ok) {
        alert('Thank you for your message! We will get back to you soon.');
        form.reset();
      } else {
        alert('Something went wrong. Please try again.');
      }
    } catch (error) {
      alert('Failed to send message. Please try again later.');
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}>
        <div className="max-w-[1400px] mx-auto px-8 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://public.readdy.ai/ai/img_res/c828217a-943a-43f8-8931-978b1665ebba.png" 
                alt="LIK d.o.o. Logo" 
                className="h-10 w-10 object-contain"
              />
              <span className="text-xl font-bold text-[#1A2332]">LIK d.o.o.</span>
            </div>
            <div className="flex items-center gap-10">
              <button onClick={() => scrollToSection('home')} className="text-[#2D3748] hover:text-[#1A2332] transition-colors text-[15px] font-medium cursor-pointer">Home</button>
              <button onClick={() => scrollToSection('about')} className="text-[#2D3748] hover:text-[#1A2332] transition-colors text-[15px] font-medium cursor-pointer">About</button>
              <button onClick={() => scrollToSection('products')} className="text-[#2D3748] hover:text-[#1A2332] transition-colors text-[15px] font-medium cursor-pointer">Products</button>
              <button onClick={() => scrollToSection('features')} className="text-[#2D3748] hover:text-[#1A2332] transition-colors text-[15px] font-medium cursor-pointer">Features</button>
              <button onClick={() => scrollToSection('contact')} className="text-[#2D3748] hover:text-[#1A2332] transition-colors text-[15px] font-medium cursor-pointer">Contact</button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="pt-32 pb-20 px-8">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-12 gap-8 items-center">
            {/* Left Column */}
            <div className="col-span-5">
              <h1 className="text-[56px] font-bold text-[#0A0E27] leading-[1.1] mb-8">
                Modern iOS<br />
                Applications for<br />
                <span className="ml-8">Mobile Security</span>
              </h1>
              <p className="text-[18px] text-[#6B7280] leading-relaxed">
                We are a team of experts developing cutting-edge applications<br />
                for the iOS platform, enhancing user experience and security.
              </p>
            </div>

            {/* Middle Column */}
            <div className="col-span-4">
              <div className="rounded-[32px] bg-gradient-to-br from-[#E0E7FF] to-[#EDE9FE] p-12 shadow-lg h-[600px] flex items-center justify-center">
                <img 
                  src="https://public.readdy.ai/ai/img_res/0d8b4d9e60c874cded1d2a0823569053.jpg"
                  alt="Mobile Security"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>

            {/* Right Column */}
            <div className="col-span-3">
              <p className="text-[14px] uppercase tracking-wider text-[#6B7280] mb-6 font-semibold">Our Focus</p>
              <div className="grid grid-cols-2 gap-6">
                <div className="flex flex-col items-center">
                  <div className="w-20 h-20 rounded-full bg-[#D1FAE5] flex items-center justify-center mb-3">
                    <i className="ri-shield-keyhole-line text-3xl text-[#059669]"></i>
                  </div>
                  <span className="text-[12px] text-[#6B7280] text-center">VPN Security</span>
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-20 h-20 rounded-full bg-[#FED7AA] flex items-center justify-center mb-3">
                    <i className="ri-lock-line text-3xl text-[#EA580C]"></i>
                  </div>
                  <span className="text-[12px] text-[#6B7280] text-center">Data Protection</span>
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-20 h-20 rounded-full bg-[#BFDBFE] flex items-center justify-center mb-3">
                    <i className="ri-delete-bin-line text-3xl text-[#2563EB]"></i>
                  </div>
                  <span className="text-[12px] text-[#6B7280] text-center">Device Cleaner</span>
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-20 h-20 rounded-full bg-[#E9D5FF] flex items-center justify-center mb-3">
                    <i className="ri-speed-line text-3xl text-[#9333EA]"></i>
                  </div>
                  <span className="text-[12px] text-[#6B7280] text-center">Performance</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-8 bg-white">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-12 gap-16 items-center">
            {/* Left Content */}
            <div className="col-span-6">
              <div className="inline-block px-5 py-2 border border-[#E5E7EB] rounded-full mb-8">
                <span className="text-[14px] text-[#6B7280]">About Us</span>
              </div>
              <h2 className="text-[48px] font-bold text-[#0A0E27] leading-[1.2] mb-10">
                Expert Team<br />
                <span className="font-light italic">Developing</span><br />
                Modern Solutions<br />
                for iOS Platform
              </h2>
              <div className="space-y-6 text-[16px] text-[#6B7280] leading-[1.8]">
                <p>
                  LIK d.o.o. is a dedicated team of professionals specializing in iOS application development. We focus on creating innovative solutions that enhance mobile device security and improve overall user experience.
                </p>
                <p>
                  Our flagship products include advanced VPN services and comprehensive device cleaning tools, designed with the modern user in mind. We combine cutting-edge technology with intuitive design to deliver applications that are both powerful and easy to use.
                </p>
                <p>
                  With years of experience in mobile development, we understand the importance of privacy, security, and performance. Our mission is to provide iOS users with reliable tools that protect their digital lives while maintaining optimal device performance.
                </p>
              </div>
            </div>

            {/* Right Image */}
            <div className="col-span-6">
              <div className="rounded-[28px] bg-gradient-to-br from-[#FEF3C7] to-[#FDE68A] p-5 shadow-lg">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20software%20development%20team%20working%20together%20in%20modern%20bright%20office%20space%2C%20developers%20collaborating%20on%20iOS%20mobile%20applications%2C%20clean%20minimalist%20workspace%20with%20computers%20and%20devices%2C%20warm%20natural%20lighting%2C%20contemporary%20tech%20company%20environment%2C%20team%20collaboration%20atmosphere&width=700&height=600&seq=about001&orientation=landscape"
                  alt="Our Team"
                  className="w-full h-[500px] object-cover rounded-[20px]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-20 px-8 bg-[#FAFBFC]">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex items-end justify-between mb-12">
            <div>
              <h2 className="text-[52px] font-bold text-[#0A0E27]">
                Our <span className="text-[#DC2626]">Products</span>
              </h2>
              <p className="text-[16px] text-[#6B7280] mt-2">VPN & Cleaner Applications</p>
            </div>
            <div className="px-6 py-2 bg-[#0A0E27] text-white rounded-full text-[14px]">
              iOS Platform
            </div>
          </div>

          <div className="grid grid-cols-4 gap-6" data-product-shop>
            {/* VPN Product */}
            <div className="rounded-[24px] bg-[#EBF5FF] overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300 cursor-pointer">
              <div className="h-[360px] p-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20iOS%20VPN%20application%20interface%20screenshot%20showing%20secure%20connection%20status%20with%20shield%20icon%2C%20clean%20minimalist%20UI%20design%2C%20blue%20and%20white%20color%20scheme%2C%20professional%20mobile%20app%20screen%2C%20connection%20toggle%20button%2C%20server%20location%20display%2C%20simple%20background&width=320&height=360&seq=vpn001&orientation=portrait"
                  alt="VPN Security"
                  className="w-full h-full object-cover rounded-[16px]"
                />
              </div>
              <div className="bg-white p-6">
                <h3 className="text-[20px] font-bold text-[#0A0E27] mb-2">VPN Security</h3>
                <p className="text-[14px] text-[#6B7280] mb-4">Secure and private internet connection for your iOS device</p>
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#EBF5FF] flex items-center justify-center">
                    <i className="ri-shield-check-line text-[#2563EB]"></i>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-[#EBF5FF] flex items-center justify-center">
                    <i className="ri-global-line text-[#2563EB]"></i>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-[#EBF5FF] flex items-center justify-center">
                    <i className="ri-lock-line text-[#2563EB]"></i>
                  </div>
                </div>
              </div>
            </div>

            {/* Cleaner Product */}
            <div className="rounded-[24px] bg-[#ECFDF5] overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300 cursor-pointer">
              <div className="h-[360px] p-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20iOS%20cleaner%20application%20interface%20showing%20storage%20optimization%20and%20junk%20file%20removal%2C%20clean%20minimalist%20design%20with%20green%20accents%2C%20storage%20usage%20visualization%2C%20delete%20and%20clean%20buttons%2C%20professional%20mobile%20app%20interface%2C%20simple%20background&width=320&height=360&seq=cleaner001&orientation=portrait"
                  alt="Device Cleaner"
                  className="w-full h-full object-cover rounded-[16px]"
                />
              </div>
              <div className="bg-white p-6">
                <h3 className="text-[20px] font-bold text-[#0A0E27] mb-2">Device Cleaner</h3>
                <p className="text-[14px] text-[#6B7280] mb-4">Optimize storage and boost your device performance</p>
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#ECFDF5] flex items-center justify-center">
                    <i className="ri-delete-bin-line text-[#059669]"></i>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-[#ECFDF5] flex items-center justify-center">
                    <i className="ri-dashboard-line text-[#059669]"></i>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-[#ECFDF5] flex items-center justify-center">
                    <i className="ri-speed-up-line text-[#059669]"></i>
                  </div>
                </div>
              </div>
            </div>

            {/* Performance Tools */}
            <div className="rounded-[24px] bg-[#F5F3FF] overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300 cursor-pointer">
              <div className="h-[360px] p-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20iOS%20performance%20monitoring%20app%20interface%20showing%20CPU%20usage%2C%20memory%20stats%2C%20battery%20optimization%2C%20purple%20gradient%20design%2C%20clean%20dashboard%20with%20graphs%20and%20metrics%2C%20professional%20mobile%20application%20screen%2C%20simple%20background&width=320&height=360&seq=performance001&orientation=portrait"
                  alt="Performance Monitor"
                  className="w-full h-full object-cover rounded-[16px]"
                />
              </div>
              <div className="bg-white p-6">
                <h3 className="text-[20px] font-bold text-[#0A0E27] mb-2">Performance Tools</h3>
                <p className="text-[14px] text-[#6B7280] mb-4">Monitor and optimize your device performance</p>
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#F5F3FF] flex items-center justify-center">
                    <i className="ri-line-chart-line text-[#9333EA]"></i>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-[#F5F3FF] flex items-center justify-center">
                    <i className="ri-cpu-line text-[#9333EA]"></i>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-[#F5F3FF] flex items-center justify-center">
                    <i className="ri-battery-charge-line text-[#9333EA]"></i>
                  </div>
                </div>
              </div>
            </div>

            {/* Privacy Features */}
            <div className="rounded-[24px] bg-[#FFF7ED] overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300 cursor-pointer">
              <div className="h-[360px] p-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20iOS%20privacy%20protection%20app%20interface%20showing%20data%20encryption%20and%20privacy%20controls%2C%20warm%20orange%20peach%20color%20scheme%2C%20security%20dashboard%20with%20privacy%20settings%2C%20professional%20mobile%20app%20design%2C%20simple%20background&width=320&height=360&seq=privacy001&orientation=portrait"
                  alt="Privacy Protection"
                  className="w-full h-full object-cover rounded-[16px]"
                />
              </div>
              <div className="bg-white p-6">
                <h3 className="text-[20px] font-bold text-[#0A0E27] mb-2">Privacy Shield</h3>
                <p className="text-[14px] text-[#6B7280] mb-4">Advanced privacy protection for your personal data</p>
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#FFF7ED] flex items-center justify-center">
                    <i className="ri-eye-off-line text-[#EA580C]"></i>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-[#FFF7ED] flex items-center justify-center">
                    <i className="ri-file-shield-line text-[#EA580C]"></i>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-[#FFF7ED] flex items-center justify-center">
                    <i className="ri-fingerprint-line text-[#EA580C]"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-8 bg-[#F9FAFB]">
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-16">
            <p className="text-[14px] text-[#6B7280] mb-3 font-serif">Why Choose Us</p>
            <h2 className="text-[52px] font-bold text-[#0A0E27] leading-[1.1]">
              Advanced Features<br />
              <span className="font-light italic text-[48px]">for Your Security</span>
            </h2>
          </div>

          <div className="grid grid-cols-4 gap-8">
            {/* Security Feature */}
            <div className="rounded-[28px] bg-[#FFF1F2] overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer">
              <div className="h-[340px] p-8 flex items-center justify-center">
                <img 
                  src="https://readdy.ai/api/search-image?query=minimalist%203D%20illustration%20of%20security%20shield%20with%20lock%20symbol%2C%20soft%20pink%20background%2C%20clean%20modern%20design%2C%20floating%20shield%20icon%20with%20glow%20effect%2C%20professional%20tech%20illustration%2C%20centered%20composition%2C%20simple%20gradient%20background&width=280&height=340&seq=feature001&orientation=portrait"
                  alt="Security"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="bg-white p-6">
                <h3 className="text-[18px] font-bold text-[#0A0E27]">Military-Grade Encryption</h3>
                <p className="text-[14px] text-[#6B7280] mt-2">Protect your data with advanced encryption technology</p>
              </div>
            </div>

            {/* Speed Feature */}
            <div className="rounded-[28px] bg-[#D1FAE5] overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer">
              <div className="h-[340px] p-8 flex items-center justify-center">
                <img 
                  src="https://readdy.ai/api/search-image?query=minimalist%203D%20illustration%20of%20speed%20optimization%20with%20lightning%20bolt%20and%20speedometer%2C%20mint%20green%20background%2C%20clean%20modern%20design%2C%20floating%20speed%20icon%20with%20motion%20effect%2C%20professional%20tech%20illustration%2C%20centered%20composition%2C%20simple%20gradient%20background&width=280&height=340&seq=feature002&orientation=portrait"
                  alt="Speed"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="bg-white p-6">
                <h3 className="text-[18px] font-bold text-[#0A0E27]">Lightning Fast Performance</h3>
                <p className="text-[14px] text-[#6B7280] mt-2">Experience blazing fast speeds without compromising security</p>
              </div>
            </div>

            {/* Privacy Feature */}
            <div className="rounded-[28px] bg-[#F3F4F6] overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer">
              <div className="h-[340px] p-8 flex items-center justify-center">
                <img 
                  src="https://readdy.ai/api/search-image?query=minimalist%203D%20illustration%20of%20privacy%20protection%20with%20eye%20shield%20and%20data%20symbols%2C%20light%20gray%20background%2C%20clean%20modern%20design%2C%20floating%20privacy%20icon%20with%20protective%20glow%2C%20professional%20tech%20illustration%2C%20centered%20composition%2C%20simple%20gradient%20background&width=280&height=340&seq=feature003&orientation=portrait"
                  alt="Privacy"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="bg-white p-6">
                <h3 className="text-[18px] font-bold text-[#0A0E27]">Complete Privacy Protection</h3>
                <p className="text-[14px] text-[#6B7280] mt-2">No logs, no tracking, complete anonymity guaranteed</p>
              </div>
            </div>

            {/* Support Feature */}
            <div className="rounded-[28px] bg-[#FEF3C7] overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer">
              <div className="h-[340px] p-8 flex items-center justify-center">
                <img 
                  src="https://readdy.ai/api/search-image?query=minimalist%203D%20illustration%20of%20customer%20support%20with%20headset%20and%20chat%20bubbles%2C%20warm%20yellow%20background%2C%20clean%20modern%20design%2C%20floating%20support%20icon%20with%20friendly%20glow%2C%20professional%20tech%20illustration%2C%20centered%20composition%2C%20simple%20gradient%20background&width=280&height=340&seq=feature004&orientation=portrait"
                  alt="Support"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="bg-white p-6">
                <h3 className="text-[18px] font-bold text-[#0A0E27]">24/7 Expert Support</h3>
                <p className="text-[14px] text-[#6B7280] mt-2">Our team is always ready to help you anytime</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="bg-[#0A0E27] rounded-t-[32px] px-8 py-16">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-12 gap-16 mb-16">
            {/* Contact Form */}
            <div className="col-span-5">
              <h2 className="text-[42px] font-light text-white mb-4">Get in Touch</h2>
              <p className="text-[16px] text-[#9CA3AF] mb-8">Have questions? We'd love to hear from you</p>
              
              <form onSubmit={handleSubmit} data-readdy-form id="contact-form" className="space-y-6">
                <div>
                  <input 
                    type="text" 
                    name="name"
                    placeholder="Your Name" 
                    required
                    className="w-full bg-transparent border-b border-[#374151] text-white placeholder-[#6B7280] py-3 focus:outline-none focus:border-[#14B8A6] transition-colors text-[15px]"
                  />
                </div>
                <div>
                  <input 
                    type="email" 
                    name="email"
                    placeholder="Your Email" 
                    required
                    className="w-full bg-transparent border-b border-[#374151] text-white placeholder-[#6B7280] py-3 focus:outline-none focus:border-[#14B8A6] transition-colors text-[15px]"
                  />
                </div>
                <div>
                  <textarea 
                    name="message"
                    placeholder="Your Message" 
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full bg-transparent border-b border-[#374151] text-white placeholder-[#6B7280] py-3 focus:outline-none focus:border-[#14B8A6] transition-colors resize-none text-[15px]"
                  ></textarea>
                </div>
                <button 
                  type="submit"
                  className="px-8 py-3 bg-[#14B8A6] text-white rounded-lg hover:bg-[#0D9488] transition-colors font-medium text-[15px] whitespace-nowrap cursor-pointer"
                >
                  Send Message
                </button>
              </form>
            </div>

            {/* Navigation Links */}
            <div className="col-span-2">
              <h3 className="text-[14px] uppercase tracking-wider text-[#9CA3AF] mb-6">Navigation</h3>
              <div className="space-y-4">
                <p className="text-white text-[15px] cursor-pointer hover:text-[#14B8A6] transition-colors">Home</p>
                <p className="text-white text-[15px] cursor-pointer hover:text-[#14B8A6] transition-colors">About</p>
                <p className="text-white text-[15px] cursor-pointer hover:text-[#14B8A6] transition-colors">Products</p>
                <p className="text-white text-[15px] cursor-pointer hover:text-[#14B8A6] transition-colors">Features</p>
              </div>
            </div>

            {/* Contact Information */}
            <div className="col-span-5">
              <h3 className="text-[14px] uppercase tracking-wider text-[#9CA3AF] mb-6">Contact</h3>
              <div className="space-y-6 mb-10">
                <div>
                  <p className="text-[13px] text-[#9CA3AF] mb-1 flex items-center gap-2">
                    <i className="ri-mail-line"></i>
                    <span>Email</span>
                  </p>
                  <a href="mailto:help@likdoo.com" className="text-white text-[15px] hover:text-[#14B8A6] transition-colors">help@likdoo.com</a>
                </div>
                <div>
                  <p className="text-[13px] text-[#9CA3AF] mb-1 flex items-center gap-2">
                    <i className="ri-phone-line"></i>
                    <span>Phone</span>
                  </p>
                  <a href="tel:+16812811387" className="text-white text-[15px] hover:text-[#14B8A6] transition-colors">+1 6812811387</a>
                </div>
              </div>

              <h3 className="text-[14px] uppercase tracking-wider text-[#9CA3AF] mb-6">Location</h3>
              <div className="space-y-2 text-white text-[15px] leading-relaxed">
                <p className="flex items-start gap-2">
                  <i className="ri-map-pin-line mt-1"></i>
                  <span>Dore Pejacevic 12</span>
                </p>
                <p className="ml-6">Petrijevci, 31208</p>
                <p className="ml-6">Croatia</p>
              </div>
            </div>
          </div>

          {/* Footer Bottom */}
          <div className="border-t border-[#374151] pt-8">
            <div className="flex items-center justify-between">
              <div className="flex gap-6">
                <span className="text-[#9CA3AF] text-[14px] cursor-pointer hover:text-white transition-colors">Twitter</span>
                <span className="text-[#9CA3AF] text-[14px] cursor-pointer hover:text-white transition-colors">LinkedIn</span>
                <span className="text-[#9CA3AF] text-[14px] cursor-pointer hover:text-white transition-colors">GitHub</span>
              </div>
              <p className="text-[#9CA3AF] text-[14px]">© 2025 LIK d.o.o. All rights reserved</p>
              <div className="flex gap-6">
                <span className="text-[#9CA3AF] text-[14px] cursor-pointer hover:text-white transition-colors">Privacy Policy</span>
                <span className="text-[#9CA3AF] text-[14px] cursor-pointer hover:text-white transition-colors">Terms of Service</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
